import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { apiClient } from "../../lib/api/client.js";
import { downloadCsv } from "../../lib/csv.js";
import type { Rilevazione, KpiResponse } from "@taras/contracts";

const COLORI: Record<string, string> = {
  OK: "#16a34a",
  PARZIALE: "#f59e0b",
  NON_CONFORME: "#dc2626",
};

const CAUSALE_LABELS: Record<string, string> = {
  BIDONE_NON_ESPOSTO: "Bidone non esposto",
  CONTENUTO_NON_CONFORME: "Contenuto non conforme",
  SOVRARIEMPIMENTO: "Sovrariempimento",
  CONTENITORE_DANNEGGIATO: "Contenitore danneggiato",
  ACCESSO_IMPEDITO: "Accesso impedito",
  ETICHETTA_ILLEGGIBILE: "Etichetta illeggibile",
  BARCODE_NON_RICONOSCIUTO: "Barcode non riconosciuto",
  ALTRO: "Altro",
};

type GeoPoint = { id: string; lat: number; lng: number; qualita_conferimento: string };

type IntestazioneKit = {
  codice_barcode: string;
  frazione_merceologica: string;
  volumetria_litri: number;
  stato: string;
  data_apposizione: string | null;
  kit: {
    codice_kit: string;
    stato: string;
    data_consegna: string | null;
    note: string | null;
    utenza: {
      codice_tari: string;
      tipo: string;
      intestatario_nominativo: string;
      intestatario_cf_piva: string | null;
      indirizzo_via: string;
      indirizzo_civico: string;
      indirizzo_cap: string;
      attiva: boolean;
      data_attivazione: string | null;
      data_cessazione: string | null;
    };
  };
};

function todayStr() { return new Date().toISOString().slice(0, 10); }
function daysAgoStr(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export function RilevazioniPage() {
  const [qualita, setQualita] = useState<string>("");
  const [dataDa, setDataDa] = useState(daysAgoStr(30));
  const [dataA, setDataA] = useState(todayStr());
  const [exporting, setExporting] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mapExpanded, setMapExpanded] = useState(false);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams({ data_da: dataDa, data_a: dataA });
      if (qualita) p.set("qualita", qualita);
      const rows = await apiClient.get<Record<string, unknown>[]>(`/api/v1/rilevazioni/export?${p}`);
      downloadCsv(`rilevazioni_${dataDa}_${dataA}.csv`, rows);
    } finally {
      setExporting(false);
    }
  };
  const mapRef = useRef<maplibregl.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);

  const { data: kpi } = useQuery<KpiResponse>({
    queryKey: ["reporting", "kpi"],
    queryFn: () => apiClient.get("/api/v1/reporting/kpi"),
  });

  const { data: rilevazioni } = useQuery<{ items: Rilevazione[]; next_cursor: string | null }>({
    queryKey: ["rilevazioni", { qualita }],
    queryFn: () => apiClient.get(`/api/v1/rilevazioni${qualita ? `?qualita=${qualita}` : ""}`),
  });

  const { data: geoPoints } = useQuery<GeoPoint[]>({
    queryKey: ["rilevazioni", "geo", { qualita }],
    queryFn: () => apiClient.get(`/api/v1/rilevazioni/geo${qualita ? `?qualita=${qualita}` : ""}`),
  });

  const selectedRilevazione = rilevazioni?.items.find((r) => r.id === selectedId) ?? null;
  const selectedGeo = geoPoints?.find((p) => p.id === selectedId) ?? null;

  const { data: intestazione } = useQuery<IntestazioneKit>({
    queryKey: ["contenitore-barcode", selectedRilevazione?.codice_barcode_letto],
    queryFn: () =>
      apiClient.get(`/api/v1/contenitori/by-barcode/${encodeURIComponent(selectedRilevazione!.codice_barcode_letto!)}`),
    enabled: !!selectedRilevazione?.codice_barcode_letto,
    retry: false,
  });

  // Inizializza mappa
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            attribution: "© OpenStreetMap contributors",
          },
        },
        layers: [{ id: "osm", type: "raster", source: "osm" }],
      },
      center: [17.2286, 40.4748],
      zoom: 13,
    });

    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-left");

    map.on("load", () => {
      map.addSource("rilevazioni", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      map.addLayer({
        id: "rilevazioni-cerchi",
        type: "circle",
        source: "rilevazioni",
        paint: {
          "circle-radius": ["case", ["==", ["get", "selected"], true], 11, 7],
          "circle-color": [
            "match", ["get", "qualita"],
            "OK", "#16a34a",
            "PARZIALE", "#f59e0b",
            "NON_CONFORME", "#dc2626",
            "#6b7280",
          ],
          "circle-stroke-color": ["case", ["==", ["get", "selected"], true], "#1e40af", "#ffffff"],
          "circle-stroke-width": ["case", ["==", ["get", "selected"], true], 3, 1.5],
          "circle-opacity": 0.9,
        },
      });

      map.on("mouseenter", "rilevazioni-cerchi", () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", "rilevazioni-cerchi", () => { map.getCanvas().style.cursor = ""; });
    });

    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Aggiorna GeoJSON
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const source = map.getSource("rilevazioni") as maplibregl.GeoJSONSource | undefined;
    if (!source) return;

    source.setData({
      type: "FeatureCollection",
      features: (geoPoints ?? []).map((p) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.lng, p.lat] },
        properties: { id: p.id, qualita: p.qualita_conferimento, selected: p.id === selectedId },
      })),
    });
  }, [geoPoints, selectedId]);

  // Resize mappa quando cambia expanded (MapLibre deve ricalcolare le dimensioni)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const t = setTimeout(() => map.resize(), 50);
    return () => clearTimeout(t);
  }, [mapExpanded]);

  // Fly to quando cambia la selezione
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedGeo) return;

    map.flyTo({
      center: [selectedGeo.lng, selectedGeo.lat],
      zoom: Math.max(map.getZoom(), 15),
      duration: 800,
    });

    // Aggiorna marker di selezione
    markerRef.current?.remove();
    const el = document.createElement("div");
    el.style.cssText = "width:22px;height:22px;border-radius:50%;border:3px solid #1e40af;background:transparent;";
    markerRef.current = new maplibregl.Marker({ element: el })
      .setLngLat([selectedGeo.lng, selectedGeo.lat])
      .addTo(map);
  }, [selectedGeo]);

  const handleRowClick = (id: string) => {
    setSelectedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex flex-wrap items-center gap-4">
        <h2 className="text-lg font-semibold text-gray-900 shrink-0">Cruscotto Rilevazioni</h2>
        <select
          value={qualita}
          onChange={(e) => { setQualita(e.target.value); setSelectedId(null); }}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Tutti gli esiti</option>
          <option value="OK">Conformi</option>
          <option value="PARZIALE">Con riserva</option>
          <option value="NON_CONFORME">Non conformi</option>
        </select>
        <div className="flex gap-3 text-xs text-gray-500">
          {(["OK", "PARZIALE", "NON_CONFORME"] as const).map((q) => (
            <span key={q} className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: COLORI[q] }} />
              {q === "OK" ? "Conforme" : q === "PARZIALE" ? "Con riserva" : "Non conforme"}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <label className="text-xs text-gray-500">Dal</label>
          <input type="date" value={dataDa} max={dataA}
            onChange={(e) => setDataDa(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <label className="text-xs text-gray-500">al</label>
          <input type="date" value={dataA} min={dataDa} max={todayStr()}
            onChange={(e) => setDataA(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button onClick={handleExportCsv} disabled={exporting}
            className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5">
            <svg viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 text-gray-500">
              <path fillRule="evenodd" d="M2 13.5a.5.5 0 0 0 .5.5h11a.5.5 0 0 0 0-1h-11a.5.5 0 0 0-.5.5ZM8 1a.5.5 0 0 1 .5.5v8.793l2.146-2.147a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 0 1 .708-.708L7.5 10.293V1.5A.5.5 0 0 1 8 1Z" clipRule="evenodd"/>
            </svg>
            {exporting ? "Esportazione…" : "Esporta CSV"}
          </button>
        </div>
        {selectedId && (
          <button onClick={() => setSelectedId(null)} className="text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2 py-1">
            Deseleziona ×
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">

      {/* KPI */}
      {kpi && (
        <div className="grid grid-cols-4 gap-4">
          <KpiCard label="Totale rilevazioni" value={kpi.totale_rilevazioni} />
          <KpiCard label="Conferimenti corretti" value={kpi.ok} color="#16a34a" />
          <KpiCard label="Con riserva" value={kpi.parziali} color="#f59e0b" />
          <KpiCard label="Non conformi" value={kpi.non_conformi} color="#dc2626" />
        </div>
      )}

      {/* Layout mappa + pannello */}
      <div className="flex gap-4">
        {/* Il container mappa è sempre nello stesso nodo DOM — cambia solo il posizionamento CSS.
            Spostarlo tra nodi diversi staccherebbe MapLibre dal canvas. */}
        <div
          className={
            mapExpanded
              ? "fixed inset-0 z-50 rounded-none border-0"
              : "relative flex-1 rounded-lg overflow-hidden border border-gray-200"
          }
          style={mapExpanded ? undefined : { height: 400 }}
        >
          <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

          {/* Street View */}
          {selectedGeo && (
            <a
              href={`https://maps.google.com/maps?q=&layer=c&cbll=${selectedGeo.lat},${selectedGeo.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-8 left-2 z-10 flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              title="Apri Street View in Google Maps"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-blue-500">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
              Street View
            </a>
          )}

          {/* Pulsante ingrandisci / riduci */}
          <button
            onClick={() => setMapExpanded((v) => !v)}
            title={mapExpanded ? "Riduci mappa" : "Ingrandisci mappa"}
            className="absolute top-2 left-2 z-10 p-1.5 bg-white border border-gray-300 rounded-md shadow text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
          >
            {mapExpanded ? (
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                <path d="M4 8V4m0 0h4M4 4l5 5M16 12v4m0 0h-4m4 0l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                <path d="M3 8V3m0 0h5M3 3l6 6M17 12v5m0 0h-5m5 0l-6-6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </div>

        {/* Pannello dettaglio */}
        {selectedRilevazione && (
          <div className="w-80 shrink-0 bg-white rounded-lg border border-gray-200 p-4 space-y-4 overflow-y-auto" style={{ maxHeight: 400 }}>
            {/* Header esito */}
            <div className="flex items-center justify-between">
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                style={{ backgroundColor: COLORI[selectedRilevazione.qualita_conferimento] ?? "#6b7280" }}
              >
                {selectedRilevazione.qualita_conferimento}
              </span>
              <span className="text-xs text-gray-400">
                {new Intl.DateTimeFormat("it-IT", { dateStyle: "short", timeStyle: "short" })
                  .format(new Date(selectedRilevazione.timestamp_client))}
              </span>
            </div>

            {/* Causale */}
            {selectedRilevazione.causale_anomalia && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                {CAUSALE_LABELS[selectedRilevazione.causale_anomalia] ?? selectedRilevazione.causale_anomalia}
              </div>
            )}

            {/* Barcode */}
            {selectedRilevazione.codice_barcode_letto && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-0.5">Barcode letto</p>
                <p className="font-mono text-xs text-gray-800">{selectedRilevazione.codice_barcode_letto}</p>
              </div>
            )}

            {/* Coordinate GPS */}
            {selectedGeo && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-0.5">Coordinate GPS</p>
                <p className="font-mono text-xs text-gray-600">
                  {selectedGeo.lat.toFixed(5)}, {selectedGeo.lng.toFixed(5)}
                </p>
              </div>
            )}

            {/* Intestazione kit */}
            {intestazione ? (
              <div className="border-t border-gray-100 pt-3 space-y-4">

                {/* Proprietario */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Proprietario kit</p>
                  <p className="text-sm font-semibold text-gray-900">{intestazione.kit.utenza.intestatario_nominativo}</p>
                  {intestazione.kit.utenza.intestatario_cf_piva && (
                    <p className="font-mono text-xs text-gray-500 mt-0.5">{intestazione.kit.utenza.intestatario_cf_piva}</p>
                  )}
                </div>

                {/* Utenza TARI */}
                <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Utenza TARI</p>
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-xs font-semibold text-gray-800">{intestazione.kit.utenza.codice_tari}</p>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                      {intestazione.kit.utenza.tipo === "DOMESTICA" ? "Domestica" : "Non domestica"}
                    </span>
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                        intestazione.kit.utenza.attiva
                          ? "bg-green-50 text-green-700"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      {intestazione.kit.utenza.attiva ? "Attiva" : "Cessata"}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">
                      {intestazione.kit.utenza.indirizzo_via} {intestazione.kit.utenza.indirizzo_civico},{" "}
                      {intestazione.kit.utenza.indirizzo_cap}
                    </p>
                  </div>
                  {intestazione.kit.utenza.data_cessazione && (
                    <p className="text-xs text-red-600">
                      Cessata il{" "}
                      {new Intl.DateTimeFormat("it-IT", { dateStyle: "short" }).format(
                        new Date(intestazione.kit.utenza.data_cessazione)
                      )}
                    </p>
                  )}
                </div>

                {/* Kit */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Kit</p>
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs font-medium text-gray-500">Codice kit</p>
                      <p className="font-mono text-xs text-gray-700">{intestazione.kit.codice_kit}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">Stato</p>
                      <p className="text-xs text-gray-700">{intestazione.kit.stato}</p>
                    </div>
                    {intestazione.kit.data_consegna && (
                      <div>
                        <p className="text-xs font-medium text-gray-500">Consegnato</p>
                        <p className="text-xs text-gray-700">
                          {new Intl.DateTimeFormat("it-IT", { dateStyle: "short" }).format(
                            new Date(intestazione.kit.data_consegna)
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contenitore */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Contenitore</p>
                  <div className="flex gap-3">
                    <div>
                      <p className="text-xs font-medium text-gray-500">Frazione</p>
                      <p className="text-xs text-gray-700">{intestazione.frazione_merceologica}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">Volume</p>
                      <p className="text-xs text-gray-700">{intestazione.volumetria_litri} L</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">Stato</p>
                      <p className="text-xs text-gray-700">{intestazione.stato}</p>
                    </div>
                  </div>
                </div>

              </div>
            ) : selectedRilevazione.codice_barcode_letto ? (
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs text-gray-400">Contenitore non registrato in anagrafica</p>
              </div>
            ) : (
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs text-gray-400">Nessun barcode associato</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabella */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Barcode</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Esito</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Causale</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Timestamp</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rilevazioni?.items.map((r) => (
              <tr
                key={r.id}
                onClick={() => handleRowClick(r.id)}
                className={`cursor-pointer transition-colors ${
                  r.id === selectedId
                    ? "bg-blue-50 hover:bg-blue-100"
                    : "hover:bg-gray-50"
                }`}
              >
                <td className="px-4 py-3 font-mono text-xs text-gray-700">
                  {r.codice_barcode_letto ?? "—"}
                </td>
                <td className="px-4 py-3">
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                    style={{ backgroundColor: COLORI[r.qualita_conferimento] ?? "#6b7280" }}
                  >
                    {r.qualita_conferimento}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600 text-xs">
                  {r.causale_anomalia ? (CAUSALE_LABELS[r.causale_anomalia] ?? r.causale_anomalia) : "—"}
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">
                  {new Intl.DateTimeFormat("it-IT", {
                    dateStyle: "short",
                    timeStyle: "short",
                  }).format(new Date(r.timestamp_client))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
  );
}

function KpiCard({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-3xl font-bold" style={{ color: color ?? "#111827" }}>
        {value.toLocaleString("it-IT")}
      </p>
    </div>
  );
}
